import React, { useEffect, useState, Fragment } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { getAsset, updateAsset } from 'state/assets/AssetActions';
import Input from 'components/Form/Input';
import * as assetTypes from 'state/assets/AssetTypes';
import isAuthenticated from 'hooks/isAuthenticated';
import {
    InputChange,
    SelectChange,
    TextAreaChange,
    FormSubmission,
    Error,
    ErrorCodes,
} from '../../types';
import Form from 'components/Form';
import Container from 'components/Container';
import FormContainer from 'components/FormContainer';
import ErrorMessage from 'components/ErrorMessage';
import FormHeader from 'components/FormHeader';
import Legend from 'components/Legend';
import styled from 'styled-components';
import TierSelect from 'components/TierSelect';
import TextArea from 'components/TextArea';
import { SubscriptionTiers } from 'state/subscription/SubscriptionTypes';
import Button from 'components/Button';
import Loading from 'components/Loading';
import BackButton from 'components/BackButton';
import { validateBytes, presentErrorBasedOnCode } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { CLEAR_SELECTED_ASSET } from 'state/assets/AssetActionTypes';
import { AppState } from 'state/store';
import { selectSelectedAsset } from 'state/assets/AssetSelectors';

interface Props extends RouteComponentProps {
    id?: string;
}

const SensationEdit: React.FC<Props> = ({ id }) => {
    const [form, setForm] = useState<assetTypes.UpdateAssetRequest>({
        name: '',
        tier: undefined,
        bytes: [],
        description: '',
    });
    const sensationToEdit: assetTypes.Asset | null = useSelector((state: AppState) =>
        selectSelectedAsset(state)
    );
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const adminRoleRequired = true;
    const authenticated: boolean = isAuthenticated(adminRoleRequired);
    const [formComplete, setFormComplete] = useState<boolean>(true);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [byteStrings, setByteStrings] = useState<string>('');
    const [byteError, setByteError] = useState<boolean>(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!!form.name && !!form.tier && !!form.description && !!byteStrings) {
            setFormComplete(true);
        } else {
            setFormComplete(false);
        }
    }, [form]);

    useEffect(() => {
        (async () => {
            if (sensationToEdit) {
                await setForm({ ...sensationToEdit });
                await setByteStrings(sensationToEdit.bytes.join(', '));
                setLoading(false);
            }
        })();
    }, [sensationToEdit]);

    useEffect(() => {
        (async () => {
            if (authenticated && id) {
                try {
                    await dispatch(getAsset(id));
                } catch (error) {
                    //if error results from missing token it will not have a json method
                    error = error.json ? await error.json() : error;
                    setError(error);
                }
            }
        })();

        return () => {
            dispatch({ type: CLEAR_SELECTED_ASSET });
        };
    }, [id]);
    const handleInput = (e: InputChange | SelectChange | TextAreaChange): void => {
        e.persist();
        setForm(form => ({ ...form, [e.target.name]: e.target.value }));
    };

    const handleBytesInput = (e: TextAreaChange): void => {
        e.persist();
        setByteStrings(e.target.value);
        setForm(form => ({ ...form, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: FormSubmission): Promise<void> => {
        e.preventDefault();
        setIsUpdating(true);
        const bytes: number[] | null = validateBytes(byteStrings, 240);
        if (authenticated && bytes) {
            try {
                await dispatch(updateAsset({ ...form, bytes }));
                byteError && setByteError(false);
            } catch (error) {
                //if error results from missing token it will not have a json method
                error = error.json ? await error.json() : error;
                setError(error);
            }
        } else {
            setByteError(true);
        }
        setIsUpdating(false);
        sensationToEdit && navigate(`/sensations/${sensationToEdit._id}`);
    };
    return (
        <Container margin="0 auto">
            <BackButton to="/sensations" />
            {loading ? (
                <Loading height="20%" width="20%" />
            ) : (
                <Fragment>
                    <FormContainer>
                        <Header>EDIT SENSATION</Header>
                        <Legend>
                            Fields with <span>*</span> are required
                        </Legend>
                        <Form onSubmit={handleSubmit}>
                            <Input
                                label="Name"
                                value={form.name}
                                name="name"
                                onChange={handleInput}
                                margin="1em auto"
                                size="full"
                                errorMessage="Sensation name is required"
                                type="text"
                                required={true}
                            />
                            <TierSelect
                                label="Tier"
                                name="tier"
                                value={form.tier}
                                onChange={handleInput}
                                required={true}>
                                <option value={SubscriptionTiers.Free}>
                                    {SubscriptionTiers.Free}
                                </option>
                                <option value={SubscriptionTiers.Paid}>
                                    {SubscriptionTiers.Paid}
                                </option>
                            </TierSelect>
                            <TextArea
                                placeholder="Sensation description"
                                label="Description"
                                value={form.description as string}
                                name="description"
                                onChange={handleInput}
                                required={true}
                            />
                            <ToolTip>
                                Please enter bytes as comma separated numbers (128, 256, etc)
                            </ToolTip>
                            <TextArea
                                placeholder="Sensation bytes"
                                label="Bytes"
                                value={byteStrings}
                                name="bytes"
                                onChange={e => handleBytesInput(e)}
                                required={true}
                                errorMessage={
                                    byteError &&
                                    'Please ensure there are 240 bytes between 0-256, whole numbers or valid hexadecimals'
                                }
                            />
                            <Button isLoading={isUpdating} disabled={!formComplete || loading}>
                                UPDATE
                            </Button>
                        </Form>
                        {error && (
                            <ErrorMessage>
                                {presentErrorBasedOnCode(error.code as ErrorCodes)}
                            </ErrorMessage>
                        )}
                    </FormContainer>
                </Fragment>
            )}
        </Container>
    );
};

export default SensationEdit;

const Header = styled(FormHeader)`
    margin: 0 0 1em 0;
`;

const ToolTip = styled.small`
    color: ${({ theme }) => theme.primaryGrey};
    display: inline-block;
`;
